import React, { useState, useMemo } from 'react';
import { PageHeader, SectionTitle, Card, Badge } from '../design-system';
import { useGetSystemCost, useGetSystemProfitability } from '../api/casecost';
import { formatCurrency, formatInteger, formatPercentage, getCustomerDisplayName } from '../utils/formatters';
import { periodRange } from '../utils/costPeriod';
import CostPeriodFilter from '../components/ui/CostPeriodFilter';
import CostBasisToggle from '../components/ui/CostBasisToggle';
import styles from './page-system-costs.module.css';

const dollars = (v) => `$${formatCurrency(v ?? 0)}`;
// Margin sign → token-driven color class (positive green, negative red).
const signClass = (v) => (v > 0 ? styles.marginPos : v < 0 ? styles.marginNeg : '');
// margin % arrives as a fraction; null when revenue is 0 (undefined %).
const pct = (v) => (v === null || v === undefined ? '—' : formatPercentage(v));

function SysStat({ label, value, estimate }) {
  return (
    <Card padding="md">
      <div className={styles.statLabel}>
        {label}{estimate && <span className={styles.estimateTag}> (est.)</span>}
      </div>
      <div className={styles.statValue}>{dollars(value)}</div>
    </Card>
  );
}

function statusVariant(status) {
  switch ((status || '').toUpperCase()) {
    case 'ACTIVE': return 'success';
    case 'ARCHIVED':
    case 'DELETED': return 'default';
    case 'ERROR': return 'error';
    default: return 'warning';
  }
}

function statusLabel(status) {
  if (!status) return 'Unknown';
  return status.replace(/_/g, ' ').toLowerCase()
    .split(' ').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function CustomerGroup({ group, isPeriod }) {
  const [open, setOpen] = useState(true);
  return (
    <Card padding="none" className={styles.custGroup}>
      <button type="button" className={styles.custHeader} onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span className={styles.custChevron} aria-hidden="true">{open ? '▾' : '▸'}</span>
        <span className={styles.custName}>{group.name}</span>
        <span className={styles.custCount}>
          {group.cases.length} case{group.cases.length === 1 ? '' : 's'}
        </span>
        <span className={styles.custTotal}>{dollars(group.total)}</span>
      </button>
      {open && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th}>Case</th>
              <th className={styles.th}>Status</th>
              {isPeriod ? (
                <>
                  <th className={`${styles.th} ${styles.num}`}>Direct</th>
                  <th className={`${styles.th} ${styles.num}`}>Overhead (est.)</th>
                  <th className={`${styles.th} ${styles.num}`}>Total</th>
                </>
              ) : (
                <>
                  <th className={`${styles.th} ${styles.num}`}>AI</th>
                  <th className={`${styles.th} ${styles.num}`}>AWS</th>
                  <th className={`${styles.th} ${styles.num}`}>Total</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {group.cases.map((c) => (
              <tr key={c.caseID}>
                <td className={styles.cell}>{c.caseName || c.caseID}</td>
                <td className={styles.cell}>
                  <Badge variant={statusVariant(c.caseStatus)}>{statusLabel(c.caseStatus)}</Badge>
                </td>
                {isPeriod ? (
                  <>
                    <td className={`${styles.cell} ${styles.num}`}>{dollars(c.direct_usd)}</td>
                    <td className={`${styles.cell} ${styles.num}`}>{dollars(c.allocated_overhead_usd)}</td>
                    <td className={`${styles.cell} ${styles.num}`}>{dollars(c.total_usd)}</td>
                  </>
                ) : (
                  <>
                    <td className={`${styles.cell} ${styles.num}`}>{dollars(c.ai_usd)}</td>
                    <td className={`${styles.cell} ${styles.num}`}>{dollars(c.aws_usd)}</td>
                    <td className={`${styles.cell} ${styles.num}`}>{dollars(c.total_usd)}</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

function ProfitStat({ label, value, pctValue, estimate, sign }) {
  return (
    <Card padding="md">
      <div className={styles.statLabel}>
        {label}{estimate && <span className={styles.estimateTag}> (est.)</span>}
      </div>
      <div className={`${styles.statValue} ${sign ? signClass(value) : ''}`}>{dollars(value)}</div>
      {pctValue !== undefined && <div className={styles.caption}>Margin {pct(pctValue)}</div>}
    </Card>
  );
}

function ProfitGroup({ group, basis }) {
  const [open, setOpen] = useState(true);
  return (
    <Card padding="none" className={styles.custGroup}>
      <button type="button" className={styles.custHeader} onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span className={styles.custChevron} aria-hidden="true">{open ? '▾' : '▸'}</span>
        <span className={styles.custName}>{group.name}</span>
        <span className={styles.custCount}>
          {group.cases.length} case{group.cases.length === 1 ? '' : 's'}
        </span>
        <span className={`${styles.custTotal} ${signClass(group.marginDirect)}`}>{dollars(group.marginDirect)}</span>
      </button>
      {open && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th}>Case</th>
              <th className={styles.th}>Status</th>
              <th className={`${styles.th} ${styles.num}`}>Revenue</th>
              <th className={`${styles.th} ${styles.num}`}>Direct margin</th>
              <th className={`${styles.th} ${styles.num}`}>Loaded margin (est.)</th>
            </tr>
          </thead>
          <tbody>
            {group.cases.map((c) => {
              const sub = c[basis] || {};
              return (
                <tr key={c.caseID}>
                  <td className={styles.cell}>{c.caseName || c.caseID}</td>
                  <td className={styles.cell}>
                    <Badge variant={statusVariant(c.caseStatus)}>{statusLabel(c.caseStatus)}</Badge>
                  </td>
                  <td className={`${styles.cell} ${styles.num}`}>{dollars(sub.revenue)}</td>
                  <td className={`${styles.cell} ${styles.num} ${signClass(sub.margin_direct)}`}>{dollars(sub.margin_direct)}</td>
                  <td className={`${styles.cell} ${styles.num} ${signClass(sub.margin_loaded)}`}>{dollars(sub.margin_loaded)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Card>
  );
}

export default function SystemCostsPage() {
  const [periodKey, setPeriodKey] = useState('all');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const { start, end } = periodRange(periodKey, customStart, customEnd);
  const { data, isLoading, error } = useGetSystemCost(start, end);
  const [basis, setBasis] = useState('accrual');
  const { data: profData } = useGetSystemProfitability(start, end);

  const isPeriod = !!(start && end);
  const cases = data?.cases || [];
  const profCases = profData?.cases || [];

  // Group cases under their owning customer (Unassigned for ownerless cases).
  const groups = useMemo(() => {
    const byCust = {};
    cases.forEach((c) => {
      const key = c.customer?.customerID || 'unassigned';
      const g = byCust[key] || (byCust[key] = {
        key,
        name: c.customer ? getCustomerDisplayName(c.customer) : 'Unassigned',
        total: 0,
        cases: [],
      });
      g.cases.push(c);
      g.total += c.total_usd || 0;
    });
    return Object.values(byCust).sort((a, b) => b.total - a.total);
  }, [cases]);

  // Group profitability cases under their owning customer, summing the selected
  // basis's revenue + direct margin for the group header / sort order.
  const profGroups = useMemo(() => {
    const byCust = {};
    profCases.forEach((c) => {
      const key = c.customer?.customerID || 'unassigned';
      const g = byCust[key] || (byCust[key] = {
        key,
        name: c.customer ? getCustomerDisplayName(c.customer) : 'Unassigned',
        revenue: 0,
        marginDirect: 0,
        cases: [],
      });
      g.cases.push(c);
      g.revenue += c[basis]?.revenue || 0;
      g.marginDirect += c[basis]?.margin_direct || 0;
    });
    return Object.values(byCust).sort((a, b) => b.revenue - a.revenue);
  }, [profCases, basis]);

  return (
    <div className={styles.page}>
      <PageHeader
        title="System Costs"
        subtitle="Internal cost across all cases — not customer pricing. Super-admins only."
      />

      <section className={styles.section}>
        <CostPeriodFilter
          periodKey={periodKey}
          customStart={customStart}
          customEnd={customEnd}
          onPeriodKey={setPeriodKey}
          onCustomStart={setCustomStart}
          onCustomEnd={setCustomEnd}
        />
      </section>

      {periodKey === 'custom' && !isPeriod && (
        <Card padding="md"><p className={styles.empty}>Pick a start and end date.</p></Card>
      )}
      {isLoading && (
        <Card padding="md"><p className={styles.empty}>Loading system cost…</p></Card>
      )}
      {error && (
        <Card padding="md"><p className={styles.empty}>Could not load system cost.</p></Card>
      )}

      {data && (
        <>
          <section className={styles.section}>
            <SectionTitle>{isPeriod ? 'Period total, all cases' : 'Lifetime, all cases'}</SectionTitle>
            <div className={styles.statGrid}>
              {isPeriod ? (
                <>
                  <SysStat label="Direct (measured)" value={data.direct_usd} />
                  <SysStat label="Allocated overhead" value={data.allocated_overhead_usd} estimate />
                  <SysStat label="Total" value={data.total_usd} />
                </>
              ) : (
                <>
                  <SysStat label="AI / Anthropic" value={data.ai_usd} />
                  <SysStat label="AWS Infrastructure" value={data.aws_usd} />
                  <SysStat label="Total" value={data.total_usd} />
                </>
              )}
            </div>
            <p className={styles.caption}>
              {isPeriod ? (
                <>
                  Direct = each case's measured AI + tagged-AWS spend in the period. Allocated
                  overhead is an <strong>estimate</strong> of shared cost (NAT, primary DB, fleet,
                  Support, Textract): Textract split by each case's OCR pages, everything else by
                  AI-token share. Period figures begin when monthly snapshots started accruing.
                </>
              ) : (
                <>
                  Lifetime cost from the durable per-case ledger ({formatInteger(data.case_count)} case
                  {data.case_count === 1 ? '' : 's'}), incl. archived/deleted. AI cost is all-time; AWS
                  infrastructure is captured per month and summed — all-time from when cost tracking began
                  (May 2026). Pick a period above to see spend over time with shared overhead allocated.
                </>
              )}
            </p>
          </section>

          <section className={styles.section}>
            <SectionTitle>Cost by customer &amp; case</SectionTitle>
            {groups.length === 0 ? (
              <Card padding="md"><p className={styles.empty}>No case cost recorded for this selection yet.</p></Card>
            ) : (
              <div className={styles.groups}>
                {groups.map((g) => <CustomerGroup key={g.key} group={g} isPeriod={isPeriod} />)}
              </div>
            )}
          </section>

          {profData && (
            <section className={styles.section}>
              <div className={styles.profHeader}>
                <SectionTitle>Profitability</SectionTitle>
                <CostBasisToggle basis={basis} onBasis={setBasis} />
              </div>
              <div className={styles.statGrid}>
                <ProfitStat label="Revenue (billed)" value={profData[basis].revenue} />
                <ProfitStat
                  label="Direct margin"
                  value={profData[basis].margin_direct}
                  pctValue={profData[basis].margin_pct_direct}
                  sign
                />
                <ProfitStat
                  label="Fully-loaded margin"
                  value={profData[basis].margin_loaded}
                  pctValue={profData[basis].margin_pct_loaded}
                  sign
                  estimate
                />
              </div>
              <p className={styles.caption}>
                Billed revenue (from the billing ledger) minus internal cost across all cases.{' '}
                <strong>Accrual</strong> counts all months including the current open month;{' '}
                <strong>Cash</strong> counts only finalized (closed) months. <strong>Fully-loaded</strong>{' '}
                also subtracts an <strong>estimated</strong> share of shared overhead. Group totals show direct
                margin. Internal — not customer pricing.
              </p>
              {profGroups.length === 0 ? (
                <Card padding="md"><p className={styles.empty}>No revenue or cost recorded for this selection yet.</p></Card>
              ) : (
                <div className={styles.groups}>
                  {profGroups.map((g) => <ProfitGroup key={g.key} group={g} basis={basis} />)}
                </div>
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
}
